/**
 * Teste de entrega do e-mail, regra a regra, feito no navegador.
 *
 * Decisoes nao obvias:
 *  1. E um checklist LOCAL e explicado, nao uma promessa: quem decide caixa
 *     de entrada e o provedor. A tela chama o resultado de "caixa provavel"
 *     e diz que a estimativa e por regras.
 *  2. Autenticacao do dominio (SPF, DKIM, DMARC) vem do servidor
 *     (`EmailDomainHealth`); conteudo (assunto, links, personalizacao,
 *     tamanho) e checado aqui, na hora, enquanto a pessoa digita.
 *  3. Assunto tem DOIS limites: ate 40 caracteres cabe inteiro no celular;
 *     ate 60 cabe no computador. Passou disso, corta em todo lugar.
 *  4. Nota de 0 a 10: cada falha tira 3, cada aviso tira 1. Falha de
 *     autenticacao manda a caixa provavel para spam, sem discussao.
 */

import type { DnsCheck, EmailDomainHealth } from './types';

export type NivelCheck = DnsCheck;

export interface CheckEntrega {
  readonly chave: string;
  readonly rotulo: string;
  readonly nivel: NivelCheck;
  readonly detalhe: string;
}

export type CaixaProvavel = 'principal' | 'promocoes' | 'spam';

export interface ResultadoEntrega {
  readonly nota: number;
  readonly checks: readonly CheckEntrega[];
  readonly caixa: CaixaProvavel;
}

export const LIMITE_ASSUNTO_CELULAR = 40;
export const LIMITE_ASSUNTO = 60;
export const LIMITE_PREHEADER = 90;

export type FaixaAssunto = 'vazio' | 'ideal' | 'longo' | 'cortado';

export function medirAssunto(texto: string): { readonly caracteres: number; readonly faixa: FaixaAssunto } {
  const caracteres = Array.from(texto.trim()).length;
  const faixa: FaixaAssunto = caracteres === 0 ? 'vazio' : caracteres <= LIMITE_ASSUNTO_CELULAR ? 'ideal' : caracteres <= LIMITE_ASSUNTO ? 'longo' : 'cortado';
  return { caracteres, faixa };
}

/** Como o celular mostra o assunto na lista: corta e poe reticencias. */
export function cortarParaCelular(texto: string, limite: number = LIMITE_ASSUNTO_CELULAR): string {
  const letras = Array.from(texto.trim());
  return letras.length <= limite ? letras.join('') : `${letras.slice(0, limite - 1).join('').trimEnd()}…`;
}

const GATILHOS_DE_SPAM: readonly string[] = ['gratis', 'grátis', 'imperdivel', 'imperdível', 'urgente', '100%', 'ganhe', 'clique aqui', 'promoção', 'promocao', '$$$', 'dinheiro fácil'];

function contarMaiusculas(texto: string): number {
  const letras = Array.from(texto).filter((c) => c.toLowerCase() !== c.toUpperCase());
  if (letras.length === 0) {
    return 0;
  }
  return Math.round((letras.filter((c) => c === c.toUpperCase()).length * 100) / letras.length);
}

export interface ConteudoDoEmail {
  readonly subject: string;
  readonly preheader: string;
  readonly body: string;
  readonly ctaLabel: string;
  readonly ctaUrl: string;
}

function checkDns(rotulo: string, chave: string, valor: DnsCheck | null, ok: string, aviso: string, falha: string): CheckEntrega {
  if (valor === null) {
    return { chave, rotulo, nivel: 'aviso', detalhe: 'Domínio da empresa ainda não verificado.' };
  }
  return { chave, rotulo, nivel: valor, detalhe: valor === 'ok' ? ok : valor === 'aviso' ? aviso : falha };
}

export function checarEntrega(conteudo: ConteudoDoEmail, dominio: EmailDomainHealth | null): ResultadoEntrega {
  const checks: CheckEntrega[] = [];

  checks.push(checkDns('SPF', 'spf', dominio?.spf ?? null, 'O provedor está autorizado a enviar pelo domínio.', 'SPF com inclusões demais: pode falhar em alguns provedores.', 'SPF ausente: Gmail e Outlook tratam como suspeito.'));
  checks.push(checkDns('DKIM', 'dkim', dominio?.dkim ?? null, 'Assinatura do e-mail confere com o domínio.', 'DKIM publicado, mas ainda propagando.', 'Sem DKIM, a mensagem chega sem assinatura e perde confiança.'));
  checks.push(
    checkDns(
      'DMARC',
      'dmarc',
      dominio?.dmarc ?? null,
      `Política ${dominio?.dmarcPolicy ?? ''} publicada: protege a marca contra falsificação.`,
      'Política p=none: os provedores aceitam, mas não protegem a marca. Suba para quarantine.',
      'Sem DMARC: desde 2024 Gmail e Yahoo exigem para envio em volume.',
    ),
  );

  const assunto = medirAssunto(conteudo.subject);
  checks.push({
    chave: 'assunto-tamanho',
    rotulo: 'Tamanho do assunto',
    nivel: assunto.faixa === 'ideal' ? 'ok' : assunto.faixa === 'longo' ? 'aviso' : 'falha',
    detalhe:
      assunto.faixa === 'vazio'
        ? 'Escreva o assunto.'
        : assunto.faixa === 'ideal'
          ? `${assunto.caracteres} caracteres: cabe inteiro no celular.`
          : assunto.faixa === 'longo'
            ? `${assunto.caracteres} caracteres: corta no celular depois do ${LIMITE_ASSUNTO_CELULAR}º.`
            : `${assunto.caracteres} caracteres: corta até no computador.`,
  });

  const textoAssunto = conteudo.subject.toLowerCase();
  const gatilhos = GATILHOS_DE_SPAM.filter((g) => textoAssunto.includes(g) || conteudo.body.toLowerCase().includes(g));
  const caixaAlta = contarMaiusculas(conteudo.subject);
  const exclamacoes = (conteudo.subject.match(/!/g) ?? []).length;
  const problemasDeTom = [
    gatilhos.length > 0 ? `palavras que filtros marcam (${gatilhos.slice(0, 3).join(', ')})` : null,
    caixaAlta > 50 ? 'assunto em caixa alta' : null,
    exclamacoes > 1 ? 'mais de uma exclamação' : null,
  ].filter((p): p is string => p !== null);
  checks.push({
    chave: 'tom',
    rotulo: 'Tom do texto',
    nivel: problemasDeTom.length === 0 ? 'ok' : 'aviso',
    detalhe: problemasDeTom.length === 0 ? 'Sem gatilhos de spam: fala de algo que a pessoa começou.' : `Troque: ${problemasDeTom.join('; ')}.`,
  });

  checks.push({
    chave: 'preheader',
    rotulo: 'Preheader',
    nivel: conteudo.preheader.trim() === '' ? 'aviso' : Array.from(conteudo.preheader).length > LIMITE_PREHEADER ? 'aviso' : 'ok',
    detalhe:
      conteudo.preheader.trim() === ''
        ? 'Sem preheader, a caixa de entrada mostra o começo do corpo no lugar.'
        : Array.from(conteudo.preheader).length > LIMITE_PREHEADER
          ? `Mais de ${LIMITE_PREHEADER} caracteres: o final não aparece.`
          : 'Completa o assunto na lista da caixa de entrada.',
  });

  const personalizado = /\{primeiro_nome\}/.test(`${conteudo.subject} ${conteudo.body}`);
  checks.push({
    chave: 'personalizacao',
    rotulo: 'Personalização',
    nivel: personalizado ? 'ok' : 'aviso',
    detalhe: personalizado ? 'Usa {primeiro_nome}: e-mail com nome abre mais.' : 'Sem {primeiro_nome}: parece envio em massa genérico.',
  });

  const links = (conteudo.body.match(/https?:\/\/|\{link\}/g) ?? []).length + (conteudo.ctaLabel.trim() === '' ? 0 : 1);
  const urlInsegura = conteudo.ctaLabel.trim() !== '' && !/^https:\/\//i.test(conteudo.ctaUrl.trim());
  checks.push({
    chave: 'links',
    rotulo: 'Links',
    nivel: urlInsegura ? 'falha' : links > 3 ? 'aviso' : 'ok',
    detalhe: urlInsegura
      ? 'O botão precisa de um endereço https://.'
      : links > 3
        ? `${links} links: mais de 3 empurra para Promoções.`
        : links === 0
          ? 'Sem link: a pessoa não tem como concluir a compra pelo e-mail.'
          : `${links} ${links === 1 ? 'link' : 'links'}, todos com https.`,
  });

  const tamanho = Array.from(conteudo.body).length;
  checks.push({
    chave: 'tamanho',
    rotulo: 'Tamanho do corpo',
    nivel: tamanho > 5_000 ? 'aviso' : 'ok',
    detalhe: tamanho > 5_000 ? 'Texto longo: o Gmail corta mensagens grandes e esconde o descadastro.' : `${tamanho} caracteres: leitura de menos de um minuto.`,
  });

  checks.push({ chave: 'descadastro', rotulo: 'Descadastro', nivel: 'ok', detalhe: 'Link de descadastro de um clique entra sozinho no rodapé (exigência de Gmail e Yahoo).' });

  const falhas = checks.filter((c) => c.nivel === 'falha').length;
  const avisos = checks.filter((c) => c.nivel === 'aviso').length;
  const nota = Math.max(0, 10 - falhas * 3 - avisos);
  const autenticacaoFalhou = checks.some((c) => (c.chave === 'spf' || c.chave === 'dkim') && c.nivel === 'falha');
  const caixa: CaixaProvavel = autenticacaoFalhou || nota <= 3 ? 'spam' : links > 3 || gatilhos.length > 0 ? 'promocoes' : 'principal';
  return { nota, checks, caixa };
}

/** So as regras do dominio (sem conteudo): o teste do CANAL, antes de existir campanha. */
export function somenteDominio(r: ResultadoEntrega): ResultadoEntrega {
  const checks = r.checks.filter((c) => c.chave === 'spf' || c.chave === 'dkim' || c.chave === 'dmarc' || c.chave === 'descadastro');
  const falhas = checks.filter((c) => c.nivel === 'falha').length;
  const avisos = checks.filter((c) => c.nivel === 'aviso').length;
  const autenticacaoFalhou = checks.some((c) => (c.chave === 'spf' || c.chave === 'dkim') && c.nivel === 'falha');
  return { nota: Math.max(0, 10 - falhas * 3 - avisos), checks, caixa: autenticacaoFalhou ? 'spam' : 'principal' };
}
